import { basketActionsLocale } from '@epicuro-next/constants/features/localization';
import { basketActionIconsMap } from '@epicuro-next/constants/ui/icons';
import {
  IBasketAction,
  IBasketActionTypes,
  SortParams,
} from '@epicuro-next/data/baskets';
import {
  selectProductIdAdditionalPrefixRexExp,
  selectShowMaterialWidthIds,
} from '@epicuro-next/platform/configuration';
import { isNullish } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { BasketRowsGroupType } from 'graphql-api/baskets-api-interfaces';

import { SortParamsPerGroup, SortParamsTuple } from '../../models';
import { tupleToSortParams } from '../../shared/tuple-to-sort-params';
import * as fromConfiguration from '../reducers/basket-options.reducer';
import * as fromBasket from '../store';

import {
  selectActionsRule,
  selectAuxGroupingFnRule,
  selectColumnsShowRequestedOnPlaced,
  selectDefaultGroupingTypeRule,
  selectDefaultSortingRule,
  selectProductTitleLevelRule,
  selectShowIdInArticleGroup,
  selectSortingPerGroupRule,
} from './basket-configuration-rules.selectors';

export const selectOptionsState = createSelector(
  fromBasket.selectBasketState,
  (state) => state[fromConfiguration.basketOptionsFeatureKey],
);

export const selectBasketMappingSettings = createSelector(
  selectAuxGroupingFnRule,
  selectProductTitleLevelRule,
  selectProductIdAdditionalPrefixRexExp,
  selectShowIdInArticleGroup,
  selectColumnsShowRequestedOnPlaced,
  selectShowMaterialWidthIds,
  (
    auxGroupingFn,
    productTitleLevelString,
    additionalPrefix,
    showIdInArticleGroup,
    columnsShowRequestedOnPlaced,
    showMaterialWidthIds,
  ) => ({
    defaultGroupingName: auxGroupingFn,
    productTitleLevelString,
    additionalPrefix,
    showIdInArticleGroup,
    columnsShowRequestedOnPlaced,
    showMaterialWidthIds,
  }),
);

export const selectSelectedGroupingType = createSelector<
  fromBasket.State,
  [fromConfiguration.State],
  BasketRowsGroupType | null
>(selectOptionsState, (state) => state?.selectedGroupingType);

export const selectActiveGroupingType = createSelector(
  selectSelectedGroupingType,
  selectDefaultGroupingTypeRule,
  (selectedGrouping, defaultGrouping) => selectedGrouping ?? defaultGrouping,
);

export const selectActiveBasketIdResolved = createSelector<
  fromBasket.State,
  [fromConfiguration.State],
  boolean
>(selectOptionsState, (state) => state?.activeBasketIdResolved);

export const selectDefaultSortConfiguration = createSelector(
  selectDefaultSortingRule,
  (defaultSorting): Required<SortParams> => {
    if (!Array.isArray(defaultSorting)) {
      defaultSorting = [];
    }

    const { type, direction } = tupleToSortParams(
      defaultSorting as SortParamsTuple,
    );

    return {
      type: type || fromConfiguration.FALLBACK_SORT_PARAMS.type,
      direction: direction || fromConfiguration.FALLBACK_SORT_PARAMS.direction,
    };
  },
);

export const selectSortPerGroupConfiguration = createSelector(
  selectSortingPerGroupRule,
  (sortPerGroupMap): SortParamsPerGroup => {
    if (isNullish(sortPerGroupMap) || typeof sortPerGroupMap !== 'object') {
      return {};
    }

    return sortPerGroupMap;
  },
);

export const selectActiveSortingType = createSelector(
  selectOptionsState,
  ({ activeSortingType }) => activeSortingType,
);

export const selectActiveSortingDirection = createSelector(
  selectOptionsState,
  ({ activeSortingDirection }) => activeSortingDirection,
);

export const selectSortParameters = createSelector(
  selectActiveSortingType,
  selectActiveSortingDirection,
  (type, direction): Required<SortParams> => ({ type, direction }),
);

export const selectBasketActions = createSelector(
  selectActionsRule,
  (actions) => {
    const extendedActions = [
      ...actions,
      { actionType: IBasketActionTypes.DROP_SHIP },
      { actionType: IBasketActionTypes.SEND_PDF_CONFIRMATION },
    ] as IBasketAction[];

    return extendedActions.map(
      (action): IBasketAction => ({
        ...action,
        icon: basketActionIconsMap[action.actionType] ?? null,
        displayName: basketActionsLocale[action.actionType],
      }),
    );
  },
);
